import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import useAdminData from "@/lib/useAdminData";
import { AdminHeader, Card, Btn, Modal, Input, Textarea, Toggle, StatusBadge, Empty, ErrorBanner, Loading } from "@/components/admin/ui";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";

const EMPTY = { name: "", description: "", image: "", order: 0, active: true };

// Idempotency key for a create: a retry after a lost response must not create a
// duplicate category (the backend returns the already-created one instead).
function newRequestId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `cat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function AdminCategories() {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [version, setVersion] = useState(1);
  const [requestId, setRequestId] = useState(newRequestId);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const { data, loading, error, stale, reload } = useAdminData(
    () => api.get("/admin/categories").then((r) => r.data.categories)
  );
  const items = data ?? [];

  useEffect(() => { reload(); }, [reload]);

  const openNew = () => { setF(EMPTY); setEditId(null); setVersion(1); setRequestId(newRequestId()); setSaveError(null); setOpen(true); };
  const openEdit = (c) => { setF({ ...c }); setEditId(c.id); setVersion(c.version ?? 1); setSaveError(null); setOpen(true); };

  // Uploads through the existing Cloudflare R2 endpoint (the same one used for
  // product and banner images). The returned /api/media/r2/... value is stored on
  // the category exactly like a pasted URL, so images already in the catalogue
  // keep working with no migration.
  const uploadImage = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) { toast.error("Only JPG/PNG images are allowed"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be 10 MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("productId", "categories");
      const { data } = await api.post("/admin/uploads/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      // Nothing is reset: the entered fields and any already-uploaded image stay.
      setSaveError(apiError(e));
      toast.error(apiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      // expectedVersion => the backend refuses a silent overwrite if another admin
      // session saved this category first (409 with an explanation).
      const payload = editId
        ? { ...f, expectedVersion: version }
        : { ...f, clientRequestId: requestId };
      if (editId) await api.put(`/admin/categories/${editId}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("Saved");
      setOpen(false);          // only on success: a failed save keeps the form open
      reload();
    } catch (e) {
      // Form stays open with every field, image reference and selection intact.
      setSaveError(apiError(e));
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const loadLatest = async () => {
    if (!editId) { reload(); return; }
    try {
      const { data } = await api.get("/admin/categories");
      const latest = (data.categories || []).find((c) => c.id === editId);
      if (!latest) { toast.error("This category no longer exists"); reload(); return; }
      if (!window.confirm("Reload the latest saved version of this category? Your unsaved changes in this form will be replaced.")) return;
      setF({ ...latest });
      setVersion(latest.version ?? 1);
      setSaveError(null);
      reload();
      toast.success("Loaded the latest saved version");
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete(`/admin/categories/${id}`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <AdminHeader title="Categories" subtitle={`${items.length} categories`} action={<Btn variant="accent" onClick={openNew} data-testid="add-category-btn"><Plus className="h-4 w-4" /> Add Category</Btn>} />
      {error && <ErrorBanner className="mb-4" message={error.message} stale={stale} onRetry={reload} />}
      {loading && !data && !error ? <Loading text="Loading categories…" /> : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <Card key={c.id} className="p-4" data-testid={`category-${c.slug}`}>
            <div className="flex items-center gap-3">
              <img src={resolveMediaUrl(c.image)} alt="" onError={handleMediaError} className="h-14 w-14 rounded-lg bg-slate-50 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><p className="font-semibold text-slate-900">{c.name}</p><StatusBadge status={c.active ? "active" : "inactive"} /></div>
                <p className="text-xs text-slate-500 line-clamp-1">{c.description}</p>
                <p className="text-[11px] text-slate-400">Order: {c.order}</p>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-2">
              <button onClick={() => openEdit(c)} className="p-2 text-slate-500 hover:text-amber-600" data-testid={`edit-category-${c.slug}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(c.id)} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
        {/* Real empty state only — never because a request failed. */}
        {!error && !loading && items.length === 0 && <Empty />}
        {error && items.length === 0 && (
          <p className="text-xs text-slate-500" data-testid="categories-load-failed-note">
            Categories could not be loaded. This is not an empty catalogue — retry above.
          </p>
        )}
      </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Category" : "Add Category"}>
        <form onSubmit={save} className="space-y-4">
          {saveError && (
            <ErrorBanner
              testid="category-save-error"
              message={saveError}
              action={editId ? (
                <Btn type="button" variant="outline" onClick={loadLatest} data-testid="category-load-latest">
                  Reload latest
                </Btn>
              ) : null}
            />
          )}
          <Input label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="cat-name" />
          <Textarea label="Description" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} testid="cat-desc" />
          <div>
            <label className="text-xs font-medium text-slate-600">Category Image</label>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden"
              onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="cat-image-file" />
            <div className="flex items-center gap-3 mt-1">
              {f.image ? (
                <img src={resolveMediaUrl(f.image)} alt="" onError={handleMediaError} data-testid="cat-image-preview"
                  className="h-16 w-16 rounded-lg bg-slate-50 object-cover border border-slate-200" />
              ) : (
                <div className="h-16 w-16 rounded-lg bg-slate-50 border border-dashed border-slate-300 grid place-items-center text-slate-400" data-testid="cat-image-placeholder">
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
              <div className="flex flex-col items-start gap-1.5">
                <Btn type="button" variant="outline" onClick={() => fileRef.current?.click()} loading={uploading} data-testid="cat-image-upload-btn">
                  <Upload className="h-4 w-4" /> {f.image ? "Replace image" : "Upload image"}
                </Btn>
                {f.image && (
                  <button type="button" onClick={() => setF({ ...f, image: "" })} className="text-xs text-red-600 hover:underline" data-testid="cat-image-remove">
                    Remove image
                  </button>
                )}
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">JPG or PNG, up to 10 MB. Uploaded to the store's private image storage.</p>
            <Input label="Or paste an image URL" value={f.image || ""} onChange={(e) => setF({ ...f, image: e.target.value })} testid="cat-image" />
          </div>
          <Input label="Display Order" type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} />
          <Toggle label="Active" checked={f.active} onChange={(v) => setF({ ...f, active: v })} testid="cat-active" />
          <Btn variant="accent" type="submit" className="w-full" loading={saving} data-testid="save-category-btn">{saveError ? "Retry Save" : "Save"}</Btn>
        </form>
      </Modal>
    </div>
  );
}
